import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FEATURES } from "@/lib/features";
import { useSeo } from "@/lib/seo";
import { PublicFooter, PublicNav } from "@/components/home/PublicChrome";
import { FeatureMock } from "@/components/home/FeatureMock";
import { FinalCta } from "@/components/home/HomeSections";

const Row = ({ f, i }) => (
  <article className={`my-8 grid items-center gap-10 rounded-xl border border-slate-200 bg-white p-8 shadow-xl lg:grid-cols-2 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`features-row-${f.slug}`}>
    <div>
      <p className="font-data flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-blue-700"><f.icon className="h-4 w-4" /> {f.title}</p>
      <h2 className="font-display mt-3 text-2xl font-extrabold text-slate-900 sm:text-3xl">{f.seoTitle}</h2>
      <p className="mt-3 text-slate-600">{f.intro}</p>
      <Link to={`/features/${f.slug}`} className="mt-5 inline-flex items-center gap-1 font-semibold text-blue-700 hover:gap-2 hover:underline" data-testid={`features-learn-${f.slug}`} style={{ transition: "gap 0.15s ease" }}>
        Learn about {f.title.toLowerCase()} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
    <FeatureMock kind={f.mock} />
  </article>
);

export default function Features() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useSeo("Features | Warrant Whizz - Warrant Workflow Software for Teams",
    "Plan warrant runs, assign pre-warrant visits, record customer needs and photos, configure team types, manage lock changes, view a live map and export your work with Warrant Whizz.", "/features");
  return (
    <div className="min-h-screen bg-slate-900" data-testid="features-page">
      <PublicNav />
      <main>
        <section className="bg-slate-900 pb-12 pt-32 sm:pt-40">
          <div className="mx-auto max-w-6xl px-6">
            <p className="home-rise font-data text-xs uppercase tracking-[0.25em] text-blue-400">Features</p>
            <h1 className="home-rise font-display mt-4 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>Everything your warrant team needs, in one place.</h1>
            <p className="home-rise mt-6 max-w-xl text-base text-slate-300 sm:text-lg" style={{ animationDelay: "160ms" }}>From choosing your team types to completing a pre-warrant visit or run, here's how Warrant Whizz helps.</p>
          </div>
        </section>
        <div className="mx-auto max-w-6xl px-6">{FEATURES.map((f, i) => <Row key={f.slug} f={f} i={i} />)}</div>
        <FinalCta />
      </main>
      <PublicFooter />
    </div>
  );
}
